import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useParams, useSearch, type DefaultParams } from "wouter";
import { useTranslation } from "react-i18next";

import { mergeConversationViews, pinConversationFile } from "@/api/conversation-views";
import {
  isBrowserSessionViewConfig,
  isKanbanBoardViewConfig,
  isSingleFileViewConfig,
  isWorkflowViewConfig,
  agentViewTab,
  launchableBrowserMounts,
  viewPagePath,
  viewRefKey,
  type ViewTab,
} from "@/api/views";
import { ChildSessionPanel } from "@/components/child-session-panel";
import { useAsideViewManager } from "@/hooks/use-aside-view";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import { ScheduleRunsView } from "@/components/lazy-heavy-components";
import { BrowserSessionView } from "@/components/views/browser-session-view";
import { SqliteTableView } from "@/components/lazy-heavy-components";
import { CodePreviewSidebarView } from "@/components/views/code-preview-sidebar-view";
import { FileBrowserView } from "@/components/views/file-browser-view";
import { SingleFileView } from "@/components/views/single-file-view";
import { DocxView } from "@/components/lazy-heavy-components";
import { WikiView } from "@/components/views/wiki-view";
import { WorkflowView } from "@/components/lazy-heavy-components";
import { WorkflowRunPanel } from "@/components/lazy-heavy-components";
import { ProjectKanbanView } from "@/components/views/project-kanban-view";
import { VisualEditorView } from "@/components/lazy-heavy-components";
import { DashboardView } from "@/components/views/dashboard-view";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAuthWithTestMode } from "@/hooks/use-auth-with-test-mode";
import { useAgents } from "@/hooks/use-agents";
import { useConversationViews } from "@/hooks/use-conversation-views";
import { useConversationViewCapabilities } from "@/hooks/use-conversation-view-capabilities";
import { useIsMobile } from "@/hooks/use-media-query";
import { isSourceFileName } from "@/lib/file-links";
import { fetchWithAuth } from "@/lib/utils";

interface SessionViewContext {
  agent_id: string | null;
  title: string;
}

/** Standalone configured or conversation view route. */
export function AgentViewPage() {
  const { t } = useTranslation("chat");
  const params = useParams<DefaultParams & {
    agentId?: string;
    sessionId?: string;
    viewId?: string;
  }>();
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  const asideManager = useAsideViewManager({ pathname, search, navigate });
  const { user, isLoading: authLoading, signIn, getAccessToken } = useAuthWithTestMode();
  const { agents, loading: agentsLoading, error: agentsError } = useAgents(getAccessToken);
  const routeAgentId = params?.agentId ?? "";
  const sessionId = params?.sessionId ?? "";
  const viewId = params?.viewId ?? "";
  const isConversationRoute = Boolean(sessionId);
  const {
    views: conversationViews,
    loading: conversationLoading,
    error: conversationError,
    refresh: refreshConversationViews,
  } = useConversationViews(isConversationRoute ? sessionId : null, getAccessToken);
  const conversationViewCapabilities = useConversationViewCapabilities(
    isConversationRoute ? sessionId : null,
    getAccessToken,
  );
  const browserFilePinSupported = conversationViewCapabilities === null
    ? (isConversationRoute ? null : false)
    : conversationViewCapabilities.browserFilePinsSupported;
  const sessionIdentity = isConversationRoute && user
    ? JSON.stringify([user.id, sessionId])
    : null;
  const [sessionState, setSessionState] = useState<{
    identity: string | null;
    value: SessionViewContext | null;
    loading: boolean;
  }>(() => ({ identity: null, value: null, loading: isConversationRoute }));
  const session = sessionState.identity === sessionIdentity ? sessionState.value : null;
  const sessionLoading = sessionState.identity === sessionIdentity
    ? sessionState.loading
    : Boolean(sessionIdentity);
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const [browserPinnedFilePaths, setBrowserPinnedFilePaths] = useState<Set<string>>(
    () => new Set(),
  );
  const [pinningFilePath, setPinningFilePath] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const backendReconnectKey = useBackendReconnectKey();

  useEffect(() => {
    if (!authLoading && !user) void signIn();
  }, [authLoading, signIn, user]);

  useEffect(() => {
    setBrowserPinnedFilePaths(new Set());
    setPinningFilePath(null);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionIdentity) {
      setSessionState({ identity: null, value: null, loading: false });
      return;
    }
    const controller = new AbortController();
    // A reconnect revalidates the same session; keep its dashboard mounted
    // until the replacement response arrives. A new session or user starts fresh.
    setSessionState((current) => ({
      identity: sessionIdentity,
      value: current.identity === sessionIdentity ? current.value : null,
      loading: true,
    }));
    void fetchWithAuth(
      `/api/sessions/${encodeURIComponent(sessionId)}`,
      { headers: {}, cache: "no-store", signal: controller.signal },
      getAccessTokenRef.current,
    )
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<SessionViewContext>;
      })
      .then((value) => {
        if (!controller.signal.aborted) {
          setSessionState({ identity: sessionIdentity, value, loading: false });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setSessionState({ identity: sessionIdentity, value: null, loading: false });
        }
      });
    return () => controller.abort();
  }, [backendReconnectKey, sessionId, sessionIdentity]);

  const agentId = isConversationRoute ? session?.agent_id ?? "" : routeAgentId;
  const agent = agents.find((candidate) => candidate.id === agentId);
  const availableViews = useMemo<ViewTab[]>(() => {
    if (!agentId) return [];
    if (isConversationRoute) {
      return mergeConversationViews(
        agentId,
        agent?.views ?? [],
        sessionId,
        conversationViews,
      );
    }
    return (agent?.views ?? [])
      .slice()
      .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id))
      .map((view) => agentViewTab(agentId, view));
  }, [agent?.views, agentId, conversationViews, isConversationRoute, sessionId]);
  const viewScope = isConversationRoute ? "conversation" : "agent";
  const view = availableViews.find(
    (candidate) => candidate.ref.scope === viewScope && candidate.ref.viewId === viewId,
  );
  const loading = authLoading
    || !user
    || agentsLoading
    || (isConversationRoute && (sessionLoading || conversationLoading));

  const isBrowserFilePinned = useCallback((path: string): boolean => (
    browserPinnedFilePaths.has(path)
  ), [browserPinnedFilePaths]);

  const handlePinBrowserFile = useCallback(async (path: string): Promise<void> => {
    if (!isConversationRoute || !sessionId) {
      throw new Error(t("views.pinConversationOnly"));
    }
    if (browserPinnedFilePaths.has(path)) return;
    setPinningFilePath(path);
    try {
      const event = await pinConversationFile(sessionId, path, getAccessToken);
      if (event.session_id !== sessionId) return;
      setBrowserPinnedFilePaths((current) => {
        if (current.has(path)) return current;
        const next = new Set(current);
        next.add(path);
        return next;
      });
      try {
        await refreshConversationViews();
      } catch (error) {
        // The pin is persisted even if this immediate tab refresh misses.
        // Opening its opaque ID still renders the server-backed view.
        console.error("[ui] Failed to refresh pinned conversation view:", error);
      }
      navigate(
        `/session/${encodeURIComponent(sessionId)}/views/${encodeURIComponent(event.view_id)}`,
      );
    } catch (error) {
      console.error("[ui] Failed to pin browser file to conversation:", error);
      throw error;
    } finally {
      setPinningFilePath((current) => current === path ? null : current);
    }
  }, [
    browserPinnedFilePaths,
    getAccessToken,
    isConversationRoute,
    navigate,
    refreshConversationViews,
    sessionId,
    t,
  ]);

  if (loading && (!agent || !view)) {
    return (
      <div className="flex h-screen items-center justify-center gap-2 bg-background text-sm text-muted-foreground">
        <Spinner className="size-5" /> {t("views.loading")}
      </div>
    );
  }

  if (!agent || !view) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background p-6 text-center">
        <p className="text-sm text-destructive">
          {conversationError ?? agentsError ?? t("views.notFound")}
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate(sessionId ? `/session/${sessionId}` : "/")}>
          {t("page.backToChat")}
        </Button>
      </div>
    );
  }

  const isSingleFile = view.type === "single_file" || (
    view.type === "file_browser"
    && view.ref.scope === "conversation"
    && (isMarkdownFileName(view.initial_path) || isSourceFileName(view.initial_path) || /\.html?$/i.test(view.initial_path ?? ""))
  );
  const openSingleFileInEdit = isSingleFile
    && isSingleFileViewConfig(view.config)
    && view.config.open_in_edit === true;
  const pinnedFileEditingUnsupported = Boolean(
    view.ref.scope === "conversation"
    && view.file_editing_contract_version === 1
    && conversationViewCapabilities?.pinnedFileEditingSupported === false,
  );
  const contextTitle = isConversationRoute ? session?.title ?? agent.name : agent.name;

  return (
    <div className="flex h-screen overflow-hidden">
    <main className="min-w-0 flex-1 flex h-screen min-h-0 flex-col bg-background text-foreground">
      {view.availability === "unavailable" ? (
        <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t("views.unavailable")}
        </div>
      ) : view.type === "wiki" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <WikiView
            viewRef={view.ref}
            initialPath={view.initial_path}
            getToken={getAccessToken}
            variant="page"
          />
        </div>
      ) : isSingleFile ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <SingleFileView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            getToken={getAccessToken}
            openInEdit={openSingleFileInEdit}
            fallbackTitle={view.title}
            compact={isMobile}
            contextTitle={contextTitle}
            pinnedFileEditingUnsupported={pinnedFileEditingUnsupported}
          />
        </div>
      ) : view.type === "docx" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <DocxView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            getToken={getAccessToken}
            fallbackTitle={view.title}
            compact={isMobile}
            contextTitle={contextTitle}
          />
        </div>
      ) : view.type === "file_browser" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <FileBrowserView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            initialPath={view.initial_path}
            getToken={getAccessToken}
            variant="page"
            mounts={view.ref.scope === "agent" ? launchableBrowserMounts(agent?.views).map((item) => ({
              id: item.id, title: item.title, mount_id: item.mount_id!,
            })) : []}
            onMountChange={view.ref.scope === "agent" ? (mount) => navigate(viewPagePath({
              scope: "agent", agentId, viewId: mount.id,
            })) : undefined}
            onPinFile={browserFilePinSupported === true ? handlePinBrowserFile : undefined}
            isFilePinned={isBrowserFilePinned}
            pinningFilePath={pinningFilePath}
            pinnedFileEditingUnsupported={pinnedFileEditingUnsupported}
          />
        </div>
      ) : view.type === "code_preview_sidebar" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <CodePreviewSidebarView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            getToken={getAccessToken}
            shareAgentUrl={view.ref.scope === "agent"}
            compact={isMobile}
            contextTitle={contextTitle}
            leading={(
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => navigate(sessionId ? `/session/${sessionId}` : "/")}
                aria-label={t("page.backToChat")}
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
          />
        </div>
      ) : view.type === "visual_editor" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <VisualEditorView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            getToken={getAccessToken}
          />
        </div>
      ) : view.type === "dashboard" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <DashboardView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            getToken={getAccessToken}
          />
        </div>
      ) : view.type === "workflow" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <WorkflowView
            fileName={
              isWorkflowViewConfig(view.config)
                ? view.config.file_name
                : undefined
            }
            agentId={view.ref.agentId}
            getToken={getAccessToken}
            onOpenRun={asideManager.openWorkflowRun}
          />
        </div>
      ) : view.type === "kanban_board" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <ProjectKanbanView
            projectId={
              isKanbanBoardViewConfig(view.config)
                ? view.config.project_id
                : undefined
            }
            getToken={getAccessToken}
          />
        </div>
      ) : view.type === "browser_session" ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <BrowserSessionView
            key={viewRefKey(view.ref)}
            viewRef={view.ref}
            getToken={getAccessToken}
            title={view.title}
            config={
              isBrowserSessionViewConfig(view.config)
                ? view.config
                : undefined
            }
          />
        </div>
      ) : view.type === "schedule_runs" ? (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <ScheduleRunsView viewRef={view.ref} getToken={getAccessToken} onOpenSession={asideManager.openScheduleRun} />
        </div>
      ) : view.type === "sqlite_table" ? (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <SqliteTableView viewRef={view.ref} getToken={getAccessToken} />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t("views.typeUnavailable")}
        </div>
      )}
    </main>
    {view.type === "schedule_runs" && asideManager.aside?.kind === "schedule-run" && (
      <ChildSessionPanel
        key={asideManager.aside.sessionId}
        readOnly
        childSessionId={asideManager.aside.sessionId}
        parentSessionId={viewRefKey(view.ref)}
        parentSessionTitle={view.title}
        getAccessToken={getAccessToken}
        agentId={agentId}
        allAgents={agents}
        user={user}
        onClose={asideManager.closeScheduleRun}
        onOpenFullSession={asideManager.openScheduleRun}
        resize={asideManager.resize}
      />
    )}
    {asideManager.aside?.kind === "workflow-run" && (
      <WorkflowRunPanel taskId={asideManager.aside.taskId} getToken={getAccessToken}
        onClose={asideManager.closeAside} onOpenChildSession={asideManager.pushThread}
        onOpenTriggerSession={(id) => navigate(`/session/${encodeURIComponent(id)}`)} resize={asideManager.resize} />
    )}
    {asideManager.aside?.kind === "thread" && asideManager.stackDepth > 0 && (
      <ChildSessionPanel key={asideManager.aside.sessionId} readOnly
        childSessionId={asideManager.aside.sessionId} parentSessionId={sessionId || asideManager.aside.sessionId}
        parentSessionTitle={view.title} getAccessToken={getAccessToken} agentId={agentId}
        allAgents={agents} user={user} onClose={asideManager.closeAside} onBack={asideManager.popAside}
        onOpenFullSession={(id) => navigate(`/session/${encodeURIComponent(id)}`)} resize={asideManager.resize} />
    )}
    </div>
  );
}

function isMarkdownFileName(path: string | null | undefined): boolean {
  return Boolean(path && /\.(md|markdown|mdown|mkd)$/i.test(path));
}
